import { createContext, useState, useMemo, useContext, useCallback } from 'react';
import { useGeneral } from '../../Context/GeneralContext';

const CalidadContext = createContext();

export const CAT_CAUSAS = 'CausaRaizCalidad';
export const CAT_PARTES = 'PartesCalidad';
export const CAT_DEFECTOS = 'DefectosCalidad';
const CAT_ASOCIACIONES = 'ParteDefecto';

const LABELS = {
    [CAT_CAUSAS]: 'Causa raíz',
    [CAT_PARTES]: 'Parte',
    [CAT_DEFECTOS]: 'Tipo de defecto',
};

const ERROR_GENERICO = 'Error al guardar. Intenta de nuevo.';

/**
 * Los 400 del backend llegan en texto plano y los 500 como ProblemDetails JSON.
 * El texto de los 400 es útil tal cual (el de defectos inexistentes enumera los códigos),
 * así que se propaga sin reescribir.
 */
async function leerError(res) {
    if (res.status === 401 || res.status === 403) {
        return 'No tienes permisos para realizar esta acción.';
    }
    try {
        const texto = await res.text();
        if (!texto) return ERROR_GENERICO;
        if ((res.headers.get('content-type') || '').includes('json')) {
            const obj = JSON.parse(texto);
            return obj.detail || obj.title || ERROR_GENERICO;
        }
        return texto;
    } catch {
        return ERROR_GENERICO;
    }
}

export function CalidadProvider(props) {
    const { tokenR } = useGeneral();

    const [causas, setCausas] = useState([]);
    const [partes, setPartes] = useState([]);
    const [defectos, setDefectos] = useState([]);
    // Map<códigoParte, Set<códigoDefecto>>
    const [asociaciones, setAsociaciones] = useState(new Map());

    const [parteSeleccionada, setParteSeleccionada] = useState(null);
    const [seleccionDefectos, setSeleccionDefectos] = useState(new Set());
    const [dirty, setDirty] = useState(false);

    const [loading, setLoading] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [snackbar, setSnackbar] = useState(false);
    const [snackbarText, setSnackbarText] = useState('');
    const [snackbarError, setSnackbarError] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const getHeaders = useCallback(
        () => ({ authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' }),
        [tokenR]
    );

    const notificar = useCallback((texto, esError = false) => {
        setSnackbarText(texto);
        setSnackbarError(esError);
        setSnackbar(true);
    }, []);

    const fetchCatalogo = useCallback(
        async (categoria) => {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController?categoria=${categoria}`,
                { headers: getHeaders() }
            );
            if (!res.ok) throw new Error(`No se pudo leer el catálogo ${categoria}`);
            const obj = await res.json();
            return obj.map((item) => ({ ...item, id: item.codigo }));
        },
        [getHeaders]
    );

    const getAsociaciones = useCallback(async () => {
        try {
            const filas = await fetchCatalogo(CAT_ASOCIACIONES);
            const mapa = new Map();
            filas.forEach((fila) => {
                // nValor y nAux son float en el esquema: pueden llegar como 1 o 1.0.
                // La llave es el par (nValor, nAux); `codigo` se reasigna en cada guardado.
                const parte = parseInt(fila.nValor, 10);
                const defecto = parseInt(fila.nAux, 10);
                if (Number.isNaN(parte) || Number.isNaN(defecto)) return;
                if (!mapa.has(parte)) mapa.set(parte, new Set());
                mapa.get(parte).add(defecto);
            });
            setAsociaciones(mapa);
        } catch (err) {
            console.error(err);
        }
    }, [fetchCatalogo]);

    const getCatalogo = useCallback(
        async (categoria) => {
            try {
                const items = await fetchCatalogo(categoria);
                if (categoria === CAT_CAUSAS) setCausas(items);
                if (categoria === CAT_PARTES) setPartes(items);
                if (categoria === CAT_DEFECTOS) setDefectos(items);
            } catch (err) {
                console.error(err);
            }
        },
        [fetchCatalogo]
    );

    const cargarTodo = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                getCatalogo(CAT_CAUSAS),
                getCatalogo(CAT_PARTES),
                getCatalogo(CAT_DEFECTOS),
                getAsociaciones(),
            ]);
        } finally {
            setLoading(false);
        }
    }, [getCatalogo, getAsociaciones]);

    /**
     * Al borrar o renombrar partes/defectos las asociaciones pueden quedar desfasadas
     * (el contrato no define si el backend limpia ParteDefecto), así que se releen.
     */
    const recargarCategoria = useCallback(
        async (categoria) => {
            await getCatalogo(categoria);
            if (categoria !== CAT_CAUSAS) await getAsociaciones();
        },
        [getCatalogo, getAsociaciones]
    );

    // ── CRUD de catálogos (categoría explícita: hay tres vivas a la vez) ──

    const postItem = useCallback(
        async (categoria, nombre) => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController`,
                    {
                        method: 'POST',
                        body: JSON.stringify({ categoria, nombre }),
                        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                    }
                );
                if (!res.ok) {
                    setErrorMsg(await leerError(res));
                    return false;
                }
                notificar(`${LABELS[categoria]} creada correctamente`);
                await recargarCategoria(categoria);
                return true;
            } catch {
                setErrorMsg(ERROR_GENERICO);
                return false;
            }
        },
        [getHeaders, notificar, recargarCategoria]
    );

    const putItem = useCallback(
        async (categoria, codigo, nombre) => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController/${categoria}/${codigo}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify({ nombre }),
                        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                    }
                );
                if (!res.ok) {
                    setErrorMsg(await leerError(res));
                    return false;
                }
                notificar(`${LABELS[categoria]} actualizada correctamente`);
                await recargarCategoria(categoria);
                return true;
            } catch {
                setErrorMsg(ERROR_GENERICO);
                return false;
            }
        },
        [getHeaders, notificar, recargarCategoria]
    );

    const deleteItem = useCallback(
        async (categoria, codigo) => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController/${categoria}/${codigo}`,
                    { method: 'DELETE', headers: getHeaders() }
                );
                if (!res.ok) {
                    notificar(await leerError(res), true);
                    return false;
                }
                notificar(`${LABELS[categoria]} eliminada correctamente`);
                if (categoria === CAT_PARTES) {
                    setParteSeleccionada((prev) => (prev?.codigo === codigo ? null : prev));
                }
                if (categoria === CAT_DEFECTOS) {
                    setSeleccionDefectos((prev) => {
                        if (!prev.has(codigo)) return prev;
                        const next = new Set(prev);
                        next.delete(codigo);
                        return next;
                    });
                }
                await recargarCategoria(categoria);
                return true;
            } catch {
                notificar('Error al eliminar', true);
                return false;
            }
        },
        [getHeaders, notificar, recargarCategoria]
    );

    // ── Asociación parte → defectos ──

    const seleccionarParte = useCallback(
        (parte) => {
            setParteSeleccionada(parte);
            setSeleccionDefectos(new Set(asociaciones.get(parte?.codigo) || []));
            setDirty(false);
        },
        [asociaciones]
    );

    const toggleDefecto = useCallback((codigo) => {
        setSeleccionDefectos((prev) => {
            const next = new Set(prev);
            if (next.has(codigo)) next.delete(codigo);
            else next.add(codigo);
            return next;
        });
        setDirty(true);
    }, []);

    const descartarCambios = useCallback(() => {
        setSeleccionDefectos(new Set(asociaciones.get(parteSeleccionada?.codigo) || []));
        setDirty(false);
    }, [asociaciones, parteSeleccionada]);

    const guardarAsociaciones = useCallback(async () => {
        if (!parteSeleccionada) return false;
        setGuardando(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/MicroServiciosController/PartesDefectos`,
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        parte: parteSeleccionada.codigo,
                        defectos: [...seleccionDefectos],
                    }),
                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) {
                notificar(await leerError(res), true);
                return false;
            }
            // El backend deduplica y ordena: el estado se refresca con lo que realmente quedó.
            const data = await res.json();
            const parte = parseInt(data.parte, 10);
            const guardados = new Set((data.defectos || []).map((d) => parseInt(d, 10)));
            setAsociaciones((prev) => {
                const mapa = new Map(prev);
                mapa.set(parte, guardados);
                return mapa;
            });
            setSeleccionDefectos(guardados);
            setDirty(false);
            notificar('Defectos de la parte guardados correctamente');
            return true;
        } catch {
            notificar(ERROR_GENERICO, true);
            return false;
        } finally {
            setGuardando(false);
        }
    }, [getHeaders, notificar, parteSeleccionada, seleccionDefectos]);

    // Una parte sin renglones acepta TODOS los defectos, no ninguno.
    const parteSinRestricciones =
        !!parteSeleccionada && (asociaciones.get(parteSeleccionada.codigo)?.size || 0) === 0;

    const value = useMemo(
        () => ({
            causas,
            partes,
            defectos,
            asociaciones,
            loading,
            cargarTodo,
            postItem,
            putItem,
            deleteItem,
            parteSeleccionada,
            seleccionarParte,
            seleccionDefectos,
            toggleDefecto,
            descartarCambios,
            guardarAsociaciones,
            guardando,
            dirty,
            parteSinRestricciones,
            snackbar,
            setSnackbar,
            snackbarText,
            snackbarError,
            errorMsg,
            setErrorMsg,
        }),
        [
            causas,
            partes,
            defectos,
            asociaciones,
            loading,
            cargarTodo,
            postItem,
            putItem,
            deleteItem,
            parteSeleccionada,
            seleccionarParte,
            seleccionDefectos,
            toggleDefecto,
            descartarCambios,
            guardarAsociaciones,
            guardando,
            dirty,
            parteSinRestricciones,
            snackbar,
            snackbarText,
            snackbarError,
            errorMsg,
        ]
    );

    return <CalidadContext.Provider value={value} {...props} />;
}

export function useCalidad() {
    const context = useContext(CalidadContext);
    if (!context) throw new Error('useCalidad debe estar dentro del proveedor CalidadContext');
    return context;
}
